import { State } from "@hookstate/core";
import { useUniversalFind } from "../../hooks/universal";
import {
  clubMembersExcel,
  clubMembersList,
  clubTableList,
  clubTableListWidths,
} from "../../misc/constants";
import { Club, ClubState, MainUser, MainUserClub } from "../../types";
import { useGlobalMiscState } from "../../stores/misc";
import TableItem from "../club-table-item/ClubTableItem";
import { useClubs } from "../../hooks/club";
import Modal from "../Modal/Modal";
import { exportToExcel, updateMiscState } from "../../misc/utils";
import DefaultButton from "../default-button/DefaultButton";
import ArrowLeft from "../../assets/icons/ArrowLeft";
import { Avatar } from "../Avatar";
import { Download } from "../../assets/icons/Download";
import { useState } from "react";

type ClubTablePropsType = {
  localState: State<ClubState>;
};

const ClubTable = (props: ClubTablePropsType) => {
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: string;
  }>({
    key: null,
    direction: "asc",
  });

  const localState = props.localState;
  const state = localState.get();
  const { selectedCategoryId, search, clubType, clubTypeMeta } = state;
  const miscState = useGlobalMiscState();
  const clubMembersShow = Boolean(miscState.get("clubMembersShow"));
  const clubMembersShowItem = miscState.get("clubMembersShowItem") || {};
  const allClubsTriggerFetch = miscState.get("allClubsTriggerFetch") || 0;
  const allClubs: Club[] = useClubs(clubType?.id, allClubsTriggerFetch);

  const clubs = allClubs
    .filter((c) => {
      let retValue = true;
      if (selectedCategoryId?.length) {
        retValue =
          retValue && c.club_category_id === parseInt(selectedCategoryId);
      }
      if (search?.length) {
        retValue =
          retValue &&
          (c.name.toLowerCase().includes(search.toLowerCase()) ||
            c.description?.toLowerCase()?.includes(search.toLowerCase()));
      }
      return retValue;
    })
    .slice()
    .sort((a, b) => a.id - b.id);

  const toggleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const toggleModal = () => {
    miscState.set("clubMembersShow", !clubMembersShow);
  };

  const downloadMembers = () =>
    exportToExcel(
      `club_${clubMembersShowItem.id}_club_members`,
      clubMembersShowItem.members,
      clubMembersExcel
    )();

  return (
    <div className='overflow-x-auto'>
      <table className='w-full min-w-[970px] table-auto border-collapse mt-4 rounded-lg'>
        <thead>
          <tr className='bg-white'>
            {clubTableList.map((category, index) => {
              const foundWidth = clubTableListWidths[category];
              return (
                <th
                  key={index}
                  className='px-4 py-2 border-b text-left text-sm md:text-base whitespace-nowrap text-gray-400 font-semibold cursor-pointer'
                  onClick={() => category && toggleSort(category)}
                  style={
                    foundWidth
                      ? {
                          width: foundWidth,
                        }
                      : {}
                  }
                >
                  <span className='flex items-center gap-1'>
                    {index === 0 ? clubTypeMeta?.tableHeader : category}
                    {category && (
                      <span className='flex flex-col text-xs leading-none'>
                        <span
                          className={`cursor-pointer ${
                            sortConfig.key === category &&
                            sortConfig.direction === "asc"
                              ? "text-black"
                              : "text-gray-400"
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSort(category);
                          }}
                        >
                          ▲
                        </span>
                        <span
                          className={`cursor-pointer ${
                            sortConfig.key === category &&
                            sortConfig.direction === "desc"
                              ? "text-black"
                              : "text-gray-400"
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSort(category);
                          }}
                        >
                          ▼
                        </span>
                      </span>
                    )}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {clubs.map((item, index) => (
            <TableItem key={index} item={item} index={index} />
          ))}
        </tbody>
      </table>
      <Modal
        additionalClassName='w-[32.5%] h-[70%] rounded-xl flex flex-col p-8 overflow-y-auto'
        visible={clubMembersShow}
        setVisible={updateMiscState("clubMembersShow")}
      >
        <div className='flex flex-col overflow-y-auto gap-3'>
          <span className='font-normal text-2xl leading-[26.4px] text-valley-black'>
            Участники клуба
          </span>
          <div className='flex flex-col overflow-y-auto flex-1'>
            <table className='full table-auto border-collapse mt-4 rounded-lg overflow-auto'>
              <thead>
                <tr className='bg-white'>
                  {clubMembersList.map((category, index) => (
                    <th
                      key={index}
                      className='px-4 py-2 border-b text-left text-sm md:text-base whitespace-nowrap text-gray-400 font-semibold'
                    >
                      {category}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {clubMembersShowItem.members
                  ?.filter((m: MainUserClub) => m.club_members.status === 2)
                  ?.map((m: MainUserClub) => (
                    <tr>
                      <td className='px-4 py-2 align-center'>
                        <div className='flex items-center gap-2'>
                          <Avatar user={m} />
                          <span className='text-valley-black'>
                            {m.json_saved_data.firstname}{" "}
                            {m.json_saved_data.lastname}
                          </span>
                        </div>
                      </td>
                      <td className='px-4 py-2 align-center'>
                        <input
                          type='checkbox'
                          checked={Boolean(m.club_members?.is_admin)}
                        />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <div className='flex flex-row gap-3'>
            <DefaultButton
              buttonText='Выгрузить'
              additionalStyle='flex-1 bg-valley-dark-green text-white px-8 py-3 text-nowrap'
              Icon={Download}
              iconStyle='text-white'
              onClick={downloadMembers}
            />
            <DefaultButton
              buttonText='Вернуться'
              additionalStyle='flex-1 bg-[#5F6B88] text-white px-8 py-3 text-nowrap'
              Icon={ArrowLeft}
              onClick={toggleModal}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ClubTable;
